import { type BTLoginTypes } from "types/enum";

import { convertFileSizeToBytes, FileSizeUnit } from "utilities/file/file";
import { appendPortalTypeToUrl } from "utilities/portal/portal";

const MAX_FILE_SIZE_MB = 250;
const ALLOWED_EXTENSION = ".pdf";
const MIN_FILE_SIZE_BYTES = 500;

// Longest file name we render inside an upload error toast. ~30 chars keeps the toast to about two
// lines on the narrowest supported phone (~320px viewport, ~35-40 chars/line) so the message text
// stays readable instead of being pushed off-screen by a long, unbroken file name.
const MAX_FILE_NAME_DISPLAY_LENGTH = 30;

/**
 * Shortens a file name for display with a middle ellipsis, preserving the extension so the file
 * stays identifiable (e.g. "2026_Final_Construc…ForBuild.pdf"). File names rarely contain spaces,
 * so relying on CSS wrapping alone would overflow horizontally; truncating keeps toasts compact,
 * especially on mobile. Returns the name unchanged when it already fits within maxLength.
 */
export function truncateFileNameMiddle(
    fileName: string,
    maxLength: number = MAX_FILE_NAME_DISPLAY_LENGTH,
): string {
    if (fileName.length <= maxLength) {
        return fileName;
    }

    const ellipsis = "…";
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex === -1 ? "" : fileName.substring(lastDotIndex);
    const baseName = lastDotIndex === -1 ? fileName : fileName.substring(0, lastDotIndex);

    // Budget for the base name once the extension and ellipsis are accounted for. If the extension
    // is so long it leaves no room, fall back to a simple end-truncation of the whole name.
    const baseBudget = maxLength - extension.length - ellipsis.length;
    if (baseBudget < 2) {
        return fileName.substring(0, Math.max(0, maxLength - ellipsis.length)) + ellipsis;
    }

    const frontLength = Math.ceil(baseBudget / 2);
    const backLength = Math.floor(baseBudget / 2);
    const front = baseName.substring(0, frontLength);
    const back = baseName.substring(baseName.length - backLength);

    return `${front}${ellipsis}${back}${extension}`;
}

export interface IPlanFileValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Validates a file for plans upload. Checks extension (when validateType is true),
 * minimum file size, and maximum file size.
 */
export function validatePlanFile(
    fileName: string,
    fileSize: number,
    validateType: boolean,
): IPlanFileValidationResult {
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex === -1 ? "" : fileName.substring(lastDotIndex).toLowerCase();

    if (validateType && extension !== ALLOWED_EXTENSION) {
        const displayExtension = extension === "" ? "(no extension)" : extension;
        return {
            isValid: false,
            error: `Only PDF files are allowed. Selected: ${displayExtension}`,
        };
    }

    if (fileSize < MIN_FILE_SIZE_BYTES) {
        return {
            isValid: false,
            error: "File appears to be empty or invalid",
        };
    }

    const maxSizeBytes = convertFileSizeToBytes(MAX_FILE_SIZE_MB, FileSizeUnit.Megabyte);
    if (fileSize > maxSizeBytes) {
        const fileSizeMB = (fileSize / (1024 * 1024)).toFixed(2);
        return {
            isValid: false,
            error: `File size (${fileSizeMB} MB) exceeds ${MAX_FILE_SIZE_MB} MB limit`,
        };
    }

    return { isValid: true };
}

/**
 * Builds a direct browser URL from an orval query key tuple, appending portalType as a query string parameter.
 * Use this for plan file/thumbnail URLs loaded via img src, iframe, or window.open.
 * Couples to orval's query key format: [path, params?]. If orval changes this structure, this function must be updated.
 * @param queryKey The orval query key tuple: [path, params?]
 * @param loginType The user's login type from UserInfoContext
 * @returns The full URL with query params and portalType
 */
export function buildDirectUrlFromQueryKey(
    queryKey: readonly [string, ...unknown[]],
    loginType: BTLoginTypes,
): string {
    const [path, qsParams] = queryKey;

    const filteredParams =
        qsParams && typeof qsParams === "object"
            ? Object.entries(qsParams as Record<string, unknown>).filter(
                  ([, value]) => value !== undefined && value !== null,
              )
            : [];
    const searchString =
        filteredParams.length > 0
            ? new URLSearchParams(filteredParams as [string, string][]).toString()
            : "";
    const url = searchString ? `${path}?${searchString}` : path;
    return appendPortalTypeToUrl(url, loginType);
}

/**
 * Formats a date as yyyyMMddHHmmss for use as a rowVersion cache-busting parameter.
 * Matches the server-side format previously used in FileUtility.GetPlanThumbnailUrl.
 */
export function formatRowVersion(date: Date): string {
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");
    return (
        `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
        `${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`
    );
}
